import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnalysisLoader } from "../components/AnalysisLoader";
import {
  ChevronRight,
  UploadCloud,
  ArrowRight,
  Loader2,
  Check
} from "lucide-react";
import { useProjectStore } from "../stores/projectStore";
import { apiClient } from "@/lib/api";
import { showError } from "@/utils/toast";
import { cn } from "@/lib/utils";
import { useInventoryMode } from "@/hooks/useInventoryMode";
import logo from '../assets/images/logo-artie.png';

export default function ProjectDetailsInputPage() {
  const navigate = useNavigate();
  // This page is the one upload flow that had no guard anywhere on it. It is reached from
  // /projects/new, which both the dashboard and the project page link to, and it did two things
  // inventory mode forbids: it POSTs calculateArtieScore directly — not through use-artie-score, so
  // the hook-level guard cannot see it — and then navigates to /projects/:id, whose loader waits for
  // a score that is never coming.
  const inventoryMode = useInventoryMode();
  const { setProject, draft, updateDraft, resetDraft } = useProjectStore((state) => ({
    setProject: state.setProject,
    draft: state.draft,
    updateDraft: state.updateDraft,
    resetDraft: state.resetDraft,
  }));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  
  // Sync file input with store on mount if file exists in draft
  useEffect(() => {
    // If we have a file in the draft, we don't need to do anything specific for the input
    // as we're displaying the file name from the draft state.
    // However, the file input value itself cannot be set programmatically for security reasons.
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validate file type
      const validTypes = ['.pdf', '.docx', '.txt'];
      const isTypeValid = validTypes.some(type => file.name.toLowerCase().endsWith(type));
      
      if (!isTypeValid) {
        showError("Please upload a PDF, DOCX, or TXT file");
        return;
      }

      // Max size check (e.g. 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showError("File size too large. Please upload a smaller file.");
        return;
      }

      updateDraft({ file });
    }
  };

  const handleAnalyzeProject = async () => {
    // Validation
    if (!draft.title.trim()) {
      showError("Please enter a project title");
      return;
    }
    if (!draft.file) {
      showError("Please upload a script");
      return;
    }
    if (!draft.budget) {
      showError("Please enter a budget target");
      return;
    }

    try {
      setIsSubmitting(true);
      setShowLoader(true);

      // 1. Create Project
      const payload = {
        title: draft.title,
        logline: "", // Will be extracted or filled later
        status: "pending",
        brand_revenue_target: Number(draft.budget),
        flexibility_preference: draft.flexibility,
        flexibility_details: draft.flexibilityDetails,
        genre: [],
        format: "Feature Film",
        help_needed: [],
        budget: 0,
        target_30d_reach: 0,
        launch_date: draft.releaseDate || undefined,
        production_date: draft.productionDate || undefined,
        post_production_date: draft.postProductionDate || undefined,
      };

      const createdProject: any = await apiClient.createProject(payload);
      // @ts-ignore
      const projectId = createdProject.id || createdProject._id;

      if (!projectId) throw new Error("Project created but ID missing");

      // 2. Upload Script
      if (draft.file) {
        await apiClient.uploadScript({
          projectId,
          file: draft.file,
          title: draft.file.name,
        });
      }

      // 3. Update Store and Local Storage
        setProject(createdProject);
        resetDraft(); // Clear the draft after successful creation
        
        const savedProjects = JSON.parse(localStorage.getItem('creator-projects') || '[]');
        const updatedProjects = [...savedProjects, createdProject];
        localStorage.setItem('creator-projects', JSON.stringify(updatedProjects));
  
        // 4. Trigger Analysis — unless the score is turned off, in which case uploading the script
        // has already queued the placement agent server-side and there is nothing to trigger.
      if (!inventoryMode) {
        try {
          await apiClient.calculateArtieScore(projectId);
        } catch (e) {
          console.warn("Analysis trigger warning:", e);
          // Continue - it might have been triggered already
        }
      }

      // 5. Navigate to project page which will show loader with 3 tasks
      console.log(`✓ Navigating to project page. It will handle score calculation polling.`);
      
      // Hide loader before navigation
      setShowLoader(false);
      setIsSubmitting(false);
      
      // Small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate to project page which has loader with 3 tasks
      navigate(inventoryMode ? `/upload-thanks?projectId=${projectId}` : `/projects/${projectId}`);

    } catch (error) {
      console.error("Error creating project:", error);
      showError(error instanceof Error ? error.message : "Failed to create project");
      setShowLoader(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showLoader) {
    return <AnalysisLoader />;
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full pt-8 pr-10 pb-8 pl-10 relative">

      <div className="max-w-5xl mx-auto w-full pt-6 relative z-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8 font-ibm">
          <Link to="/dashboard" className="hover:text-foreground transition-colors">
            My Projects
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/projects/new" state={{ keepDraft: true }} className="hover:text-foreground transition-colors">
            New Project
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground font-medium">
            Details
          </span>
        </div>

        <div className="flex flex-col md:flex-row text-center md:text-left max-w-3xl mx-auto mb-10 items-center justify-center gap-8">
          <div className="w-24 h-24 relative shrink-0">
            <div className="absolute inset-0 bg-secondary rounded-full blur-xl opacity-50"></div>
            <img 
              src={logo} 
              alt="Artie" 
              className="w-full h-full rounded-full object-cover border-4 border-background shadow-xl relative z-10" 
            />
            <div className="absolute -right-8 -top-3 rotate-6 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-xl shadow-lg z-20 border-2 border-background whitespace-nowrap">
              Let's build this! 🚀
            </div>
          </div>
          <div className="flex flex-col">
            <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet mb-3">
              Project Details
            </h1>
            <p className="text-muted-foreground font-ibm text-base max-w-lg">
              Enter the core details for your new project. Artie will analyze
              your script to automatically infer genre, format, and tone.
            </p>
          </div>
        </div>

        {/* Options Grid */}
        <div className="flex flex-col gap-6 max-w-2xl mx-auto w-full">
          {/* Title */}
          <div className="space-y-2">
            <label htmlFor="project-title" className="block text-sm font-semibold text-foreground font-cabinet">
              What is your project called? <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="project-title"
              placeholder="e.g. The Midnight Shift"
              value={draft.title}
              onChange={(e) => updateDraft({ title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-ibm shadow-sm placeholder:text-muted-foreground/50"
            />
          </div>

          {/* Script Upload */}
          <div className="space-y-2">
            <div>
              <label className="block text-sm font-semibold text-foreground font-cabinet mb-1">
                Script Upload <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-muted-foreground font-ibm">
                Upload what you have right now. Artie looks for patterns and potential, not polish. Your work stays private.
              </p>
            </div>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all cursor-pointer group bg-card",
                draft.file ? "border-primary/50 bg-primary/5" : "hover:bg-secondary/50 hover:border-primary/50"
              )}
            >
              <input
                type="file"
                accept=".pdf,.docx,.txt"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileSelect}
              />
              <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm">
                {draft.file ? (
                   <Check className="w-6 h-6 text-primary" />
                ) : (
                   <UploadCloud className="w-6 h-6 text-primary" />
                )}
              </div>
              <p className="text-sm font-medium text-foreground mb-1">
                {draft.file ? draft.file.name : "Click to upload or drag and drop"}
              </p>
              <p className="text-xs text-muted-foreground">
                 {draft.file ? (
                   draft.file.size < 1024 * 1024
                    ? `${(draft.file.size / 1024).toFixed(2)} KB`
                    : `${(draft.file.size / 1024 / 1024).toFixed(2)} MB`
                 ) : "PDF, DOCX, or TXT (Max 180 pages)"}
              </p>
            </div>
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <label htmlFor="budget" className="block text-sm font-semibold text-foreground font-cabinet">
              How much do you need from brand partners? <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold">
                $
              </div>
              <input
                type="number"
                id="budget"
                placeholder="50,000"
                value={draft.budget}
                onChange={(e) => updateDraft({ budget: e.target.value })}
                className="w-full pl-8 pr-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-ibm shadow-sm placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          {/* Flexibility */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-foreground font-cabinet">
              Flexibility Preference
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className={cn(
                "relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all group bg-card",
                draft.flexibility === "very_flexible" ? "border-primary" : "border-border hover:border-primary/50"
              )}>
                <input
                  type="radio"
                  name="flexibility"
                  value="very_flexible"
                  checked={draft.flexibility === "very_flexible"}
                  onChange={() => updateDraft({ flexibility: "very_flexible" })}
                  className="peer sr-only"
                />
                <span className="font-semibold text-sm mb-1 text-foreground font-cabinet group-hover:text-primary transition-colors">
                  Very Flexible
                </span>
                <span className="text-xs text-muted-foreground leading-snug">
                  Open to most brand integrations.
                </span>
              </label>

              <label className={cn(
                "relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all group bg-card",
                draft.flexibility === "flexible_with_limits" ? "border-primary" : "border-border hover:border-primary/50"
              )}>
                <input
                  type="radio"
                  name="flexibility"
                  value="flexible_with_limits"
                  checked={draft.flexibility === "flexible_with_limits"}
                  onChange={() => updateDraft({ flexibility: "flexible_with_limits" })}
                  className="peer sr-only"
                />
                <span className="font-semibold text-sm mb-1 text-foreground font-cabinet group-hover:text-primary transition-colors">
                  Flexible w/ Limits
                </span>
                <span className="text-xs text-muted-foreground leading-snug">
                  Some creative restrictions apply.
                </span>
              </label>

              <label className={cn(
                "relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all group bg-card",
                draft.flexibility === "selective" ? "border-primary" : "border-border hover:border-primary/50"
              )}>
                <input
                  type="radio"
                  name="flexibility"
                  value="selective"
                  checked={draft.flexibility === "selective"}
                  onChange={() => updateDraft({ flexibility: "selective" })}
                  className="peer sr-only"
                />
                <span className="font-semibold text-sm mb-1 text-foreground font-cabinet group-hover:text-primary transition-colors">
                  Selective
                </span>
                <span className="text-xs text-muted-foreground leading-snug">
                  Strict guidelines on brands.
                </span>
              </label>
            </div>
            <textarea
              placeholder="Describe any specific constraints... (e.g. No alcohol brands, avoid political themes)"
              value={draft.flexibilityDetails}
              onChange={(e) => updateDraft({ flexibilityDetails: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-ibm shadow-sm text-sm h-24 resize-none placeholder:text-muted-foreground/50"
            ></textarea>
          </div>

          {/* Timeline */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-foreground font-cabinet">
              Production Timeline (Approximate)
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Production Start */}
              <div className="space-y-2">
                <span className="text-sm text-foreground font-semibold font-cabinet block">Production Begins</span>
                <input
                  type="date"
                  value={draft.productionDate}
                  onChange={(e) => updateDraft({ productionDate: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-ibm shadow-sm placeholder:text-muted-foreground/50 text-muted-foreground"
                  placeholder="Select date"
                />
              </div>

              {/* Post-Production */}
              <div className="space-y-2">
                <span className="text-sm text-foreground font-semibold font-cabinet block">Post-Production</span>
                <input
                  type="date"
                  value={draft.postProductionDate}
                  onChange={(e) => updateDraft({ postProductionDate: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-ibm shadow-sm placeholder:text-muted-foreground/50 text-muted-foreground"
                  placeholder="Select date"
                />
              </div>

              {/* Release */}
              <div className="space-y-2">
                <span className="text-sm text-foreground font-semibold font-cabinet block">Expected Release</span>
                <input
                  type="date"
                  value={draft.releaseDate}
                  onChange={(e) => updateDraft({ releaseDate: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-ibm shadow-sm placeholder:text-muted-foreground/50 text-muted-foreground"
                  placeholder="Select date"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-center pt-2 mt-8 pb-10">
          <button 
            onClick={handleAnalyzeProject}
            disabled={isSubmitting}
            className="px-8 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_4px_14px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.15)] flex items-center gap-2 font-ibm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span>Creating Project...</span>
                <Loader2 className="w-4 h-4 animate-spin" />
              </>
            ) : (
              <>
                <span>Analyze Project</span>
                <ArrowRight className="w-4 h-4 stroke-[2]" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}